import CustomPlugin from "./CustomPlugin";
import RelatedSwitcher from "../../components/custom/RelatedSwitcher";
import UrlWrapper from "../../utils/wrappers/Url";
import RelatedViewer from "../../components/custom/RelatedViewer";
import {PlaylistEventType} from "../decorators/PlaylistPluginDecorator";
import SkyClickPlayer from "../../components/custom/SkyClickPlayer";
import mergeOptions from "../../utils/wrappers/merge-options";

// load promise-based ajax framework
import axios from 'axios';

// custom player events
export type RelatedEventType =
    'RELATED_READY'
    ;



declare global {
    type SkyClickRelatedItemTitleDisplayMode =
        'visible' |
        'hover' |
        'hidden';
    interface SkyClickRelatedPluginOptions{
        src:string
        autoPlayTimer:number
        startAutoPlayCountDownAtEnd:boolean
        heading:string
        itemOptions: {
            displayTitleMode?: SkyClickRelatedItemTitleDisplayMode
            showTitleTooltip?:boolean
        }
    }
}

export default class RelatedPlugin extends CustomPlugin<SkyClickRelatedPluginOptions> {

    static readonly PLUGIN_NAME = 'related';

    protected relatedSrc: string;

    protected switcherComponent: RelatedSwitcher;
    protected viewerComponent: RelatedViewer;

    protected relatedItems: any[] = [];

    // Was player at playing, before the related plugin was appeared/shown
    protected wasPlayingBeforeShow:boolean;

    protected isReady = false;
    
    constructor(pluginName:string, skyClickPlayer:SkyClickPlayer, options:SkyClickRelatedPluginOptions){
        super(pluginName,skyClickPlayer,options);

        // extend default configuration with custom configuration
        this.options = mergeOptions({},{
            src: null,
            autoPlayTimer: 10,
            startAutoPlayCountDownAtEnd: true,
            heading: '',
            itemOptions: {
                displayTitleMode: 'hidden',
                showTitleTooltip: true
            }
        },this.options);
    }

    init() {
        // PLUGIN CODE
        // console.log('Plugin "' + RelatedPlugin.PLUGIN_NAME + '" initalized ');

        this.relatedSrc = this.options.src && UrlWrapper.getAbsoluteURL(this.options.src);

        // Init related video, when related file is loaded
        this.load().then((relatedItems) => {

            if(relatedItems){
                this.initSwitcher();
                this.initRelatedViewer();
                this.initPlaylistEndListener();

                // Trigger ready event
                this.isReady = true;
                this.skyClickPlayer.eventManager.trigger<RelatedEventType>('RELATED_READY');

            }

        });

    }

    get headingTitle(){
        return this.options.heading;
    }

    get autoPlayCountDownDuration(){
        return this.options.autoPlayTimer;
    }

    /**
     * Load related items
     */
    protected load() {
        if (this.relatedSrc) {
            return axios({
                method: 'get',
                url: this.relatedSrc,
                responseType: 'json'
            })
                .then((response) => {
                if(response.data){
                    return response.data;
                }
                return false;
            }).then((relatedItems) => {
                return this.relatedItems = relatedItems || [];
            }).catch(function(err){
                console.warn('related JSON can loaded: ', this.relatedSrc);
            });
        }
    }

    /**
     * Init switcher button to show and hide related viewer
     */
    protected initSwitcher(){
        // reset switcher, when already initialized
        if(!this.switcherComponent){

            // add button to control-layer component player
            this.switcherComponent = this.skyClickPlayer.getChild('ControlLayer').addChild('RelatedSwitcher',{
                relatedPlugin: this,
                clickHandler: this.show.bind(this),
            }) as RelatedSwitcher;
        }

    }

    protected initRelatedViewer() {
        // reset switcher, when already initialized
        this.viewerComponent && this.viewerComponent.dispose();

        // add viewer component
        this.viewerComponent = this.skyClickPlayer.addChild<SkyClickRelatedViewerComponentOptions>('RelatedViewer',{
            relatedPlugin: this,
            relatedItems: this.relatedItems,
            closeHandler: this.hide.bind(this)
        }) as RelatedViewer;
    }

    protected initPlaylistEndListener(){
        this.skyClickPlayer.eventManager.addListener<PlaylistEventType>('PLAYLIST_ENDED',(event:any,autoPlay:boolean) => {
            this.show();
            autoPlay && this.startAutoPlayCountdown();
        });
    }

    /**
     * Show related viewer
     */
    public show() {
        if(!this.isReady){
            // Delay show, when element is not ready
            this.skyClickPlayer.eventManager.addListener<RelatedEventType>('RELATED_READY',() => this.show());
        }else{
            // auto pause, when player is playing (remember before playback state)
            this.wasPlayingBeforeShow = !this.skyClickPlayer.paused();
            if(this.wasPlayingBeforeShow){
                this.skyClickPlayer.pause();
            }
            this.viewerComponent.show();
            this.switcherComponent.hide();
        }
    }

    /**
     * Hide related viewer
     */
    public hide = () => {
        if(!this.isReady){
            // Delay show, when element is not ready
            this.skyClickPlayer.eventManager.addListener<RelatedEventType>('RELATED_READY',() => this.hide());
        }else {
            // reset playback state, before showing related plugin
            if (this.wasPlayingBeforeShow) {
                this.skyClickPlayer.play();
            }
            this.viewerComponent.hide();
            this.switcherComponent.show();
        }
    }

    protected startAutoPlayCountdown(){
        if(this.options.startAutoPlayCountDownAtEnd){
            this.viewerComponent.startCountDown(() => {
                let autoPlayItem = this.getAutoPlayItem();
                document.location.href = this.getAutoPlayItem().link;
            })
        }
    }

    /**
     * Get related item, that should be open/played automatically
     * @returns {any}
     */
    protected getAutoPlayItem(){
        return this.relatedItems[0];
    }

}

// *** extend player declaration with new plugin function

// interface, that base on plugin class with additional startup function declaration
interface WithStartupDeclaration extends RelatedPlugin{
    (options?: Object): RelatedPlugin
}
// Using of Type Scripts "Module Augmentation" https://www.typescriptlang.org/docs/handbook/declaration-merging.html#module-augmentation
// Extend VideoJSPlayer declaration with extend function by plugin
// So TypeScript compiler support the usage of this dynamic added function, because this plugin is added to the
declare global {
    interface VideoJSPlayer{
        related:WithStartupDeclaration;
    }
}



// WEBPACK FOOTER //
// ./src/app/plugins/custom/RelatedPlugin.ts